import { useState, useCallback, useEffect } from 'react';
import { mentorChatsApi, MentorChat } from '@/utils/api-pb';
import { chatLogger } from '@/utils/logger';

export function useChatHistory() {
  const [chats, setChats] = useState<MentorChat[]>([]);
  const [currentChat, setCurrentChat] = useState<MentorChat | null>(null);
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const [totalMessageCount, setTotalMessageCount] = useState(0);
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);

  // Загрузка чатов
  const loadChats = useCallback(async () => {
    chatLogger.debug('Loading chats...');
    try {
      setIsLoadingChats(true);
      const chatsList = await mentorChatsApi.list();
      chatLogger.debug('Chats loaded:', chatsList.length);
      setChats(chatsList);

      // Подсчёт сообщений
      const count = chatsList.reduce((sum, chat) => sum + (chat.message_count || 0), 0);
      setTotalMessageCount(count);

      // Если есть чаты и нет текущего - выбираем последний
      if (chatsList.length > 0 && !currentChat) {
        const firstChat = chatsList[0]!;
        setCurrentChat(firstChat);
      }
    } catch (error) {
      console.error('[ChatPage] Error loading chats:', error);
    } finally {
      setIsLoadingChats(false);
    }
  }, [currentChat]);

  useEffect(() => {
    loadChats();
  }, []);

  // Выбор чата
  const selectChat = (chat: MentorChat) => {
    setCurrentChat(chat);
  };

  // Создание нового чата
  const createNewChat = async () => {
    try {
      const newChat = await mentorChatsApi.create();
      setChats((prev) => [newChat, ...prev]);
      setCurrentChat(newChat);
      return newChat;
    } catch (error) {
      console.error('Error creating chat:', error);
      return null;
    }
  };

  // Удаление чата
  const deleteChat = async (chatId: string) => {
    try {
      await mentorChatsApi.delete(chatId);
      setChats((prev) => prev.filter((c) => c.id !== chatId));

      // Если удалили текущий чат
      if (currentChat?.id === chatId) {
        const remainingChats = chats.filter((c) => c.id !== chatId);
        if (remainingChats.length > 0) {
          const firstChat = remainingChats[0]!;
          setCurrentChat(firstChat);
        } else {
          setCurrentChat(null);
        }
      }

      setChatToDelete(null);
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  // Получение заголовка чата - тема или "Чат DD.MM.YYYY"
  const getChatTitle = (chat: MentorChat) => {
    if (chat.title) {
      return chat.title;
    }
    const date = new Date(chat.created);
    return `Чат ${date.toLocaleDateString('ru-RU')}`;
  };

  return {
    chats,
    setChats,
    currentChat,
    setCurrentChat,
    isLoadingChats,
    totalMessageCount,
    setTotalMessageCount,
    chatToDelete,
    setChatToDelete,
    loadChats,
    selectChat,
    createNewChat,
    deleteChat,
    getChatTitle,
  };
}
